import React, { useState, useEffect, useRef, useCallback } from 'react';
import { InventoryService } from '../services/inventoryService';
import { useDialog } from '../context/DialogContext';
import { useAuthErrorHandler } from '../hooks/useAuthErrorHandler';
import styles from '../styles/step8ListModal.module.css';

interface Step10InventoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  user?: any;
  onListUpdate?: () => void;
}

interface InventoryEntry {
  date: string; // ISO date string (YYYY-MM-DD)
  note?: string; // Review of My Day
}

const Step10InventoryModal: React.FC<Step10InventoryModalProps> = ({ isOpen, onClose, user, onListUpdate }) => {
  const { showConfirm, showAlert, showHelp } = useDialog();
  const [entries, setEntries] = useState<InventoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [form, setForm] = useState<InventoryEntry | null>(null);
  
  // Auto-save state management
  const [savingEntry, setSavingEntry] = useState<number | null>(null);
  const [savedEntry, setSavedEntry] = useState<number | null>(null);
  const [saveError, setSaveError] = useState<number | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { handleError } = useAuthErrorHandler();
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const periodicSaveIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  const INVENTORY_TYPE = 'step10-inventories';
  const VERSION = 'default';
  const REQUIRED_COMPLETED = 5; // Need 5 completed entries to finish the action

  // Check if an entry is complete (has date and note text)
  const isEntryComplete = (entry: InventoryEntry): boolean => {
    if (!entry || typeof entry !== 'object') return false;
    
    const date = entry.date && typeof entry.date === 'string' ? entry.date.trim() : '';
    const note = entry.note && typeof entry.note === 'string' ? entry.note.trim() : '';
    
    return !!(date.length > 0 && note.length > 0);
  };

  // Get the next available date (today, or go back day by day until finding an available date)
  const getNextAvailableDate = (): string => {
    const today = new Date();
    const usedDates = new Set(entries.map(e => e.date).filter(d => d));
    
    // Start with today and go back day by day
    for (let i = 0; i < 365; i++) { // Max 365 days back
      const checkDate = new Date(today);
      checkDate.setDate(today.getDate() - i);
      const dateStr = checkDate.toISOString().split('T')[0];
      
      if (!usedDates.has(dateStr)) {
        return dateStr;
      }
    }
    
    // Fallback to today if somehow all dates are taken
    return today.toISOString().split('T')[0];
  };

  // Check if a date is already used
  const isDateUsed = (date: string, excludeIndex?: number): boolean => {
    return entries.some((entry, idx) => {
      if (excludeIndex !== undefined && idx === excludeIndex) return false;
      return entry.date === date;
    });
  };

  // Load entries when modal opens
  useEffect(() => {
    if (isOpen && user) {
      loadEntries();
    } else if (isOpen && !user) {
      setLoading(false);
      setEntries([]);
    }
  }, [isOpen, user]);

  const loadEntries = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const items = await InventoryService.getInventory(INVENTORY_TYPE, VERSION);
      // Filter out completion status items and any undefined/null items
      const inventoryEntries = items
        .filter(item => item != null && typeof item === 'object' && item._type !== 'completion_status')
        .map(item => ({
          date: item.date || '',
          note: item.note || ''
        }))
        .filter(item => item != null)
        // Sort by date, newest to oldest
        .sort((a, b) => {
          if (!a.date || !b.date) return 0;
          return b.date.localeCompare(a.date); // Descending order (newest first)
        });
      setEntries(inventoryEntries);
    } catch (error) {
      // If no entries exist yet, start with empty array
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  // Immediate save function (bypasses debounce) - used for critical operations
  const immediateSaveEntry = useCallback(async (idx: number, formData: InventoryEntry): Promise<boolean> => {
    return new Promise((resolve) => {
      // Use functional update to get the latest entries state
      setEntries(currentEntries => {
        // Validate index
        if (idx < 0 || idx >= currentEntries.length) {
          console.error('Invalid index for save:', idx, 'array length:', currentEntries.length);
          resolve(false);
          return currentEntries; // Return unchanged
        }
        
        // Check if data actually changed
        const hasChanges = JSON.stringify(formData) !== JSON.stringify(currentEntries[idx]);
        if (!hasChanges) {
          resolve(true);
          return currentEntries; // Return unchanged
        }

        // Create updated array
        const newData = [...currentEntries];
        newData[idx] = formData;
        
        // Filter out any undefined/null items and ensure all items are valid objects
        const validData = newData.filter(item => item != null && typeof item === 'object');

        // Save asynchronously
        InventoryService.saveInventory(INVENTORY_TYPE, VERSION, validData)
          .then(() => {
            // Clear unsaved changes flag
            setHasUnsavedChanges(false);

            // Notify parent to update counters
            if (onListUpdate) {
              onListUpdate();
            }

            resolve(true);
          })
          .catch((error: any) => {
            console.error('Error saving entry:', error);
            
            // Check if it's an authentication error
            const errorMessage = error?.message || String(error);
            const isAuthError = errorMessage.includes('401') || 
                               errorMessage.includes('Not logged in') ||
                               errorMessage.includes('Token expired') ||
                               errorMessage.includes('Invalid token') ||
                               errorMessage.includes('No authentication token');
            
            if (isAuthError) {
              showAlert({
                title: 'Session Expired',
                message: 'Your session has expired. Please sign out and sign back in to continue saving changes.',
                okText: 'OK'
              });
            }
            
            resolve(false);
          });

        // Return updated entries immediately
        return validData;
      });
    });
  }, [onListUpdate, showAlert]);

  // Auto-save function with debouncing
  const autoSaveEntry = useCallback(async (idx: number, formData: InventoryEntry) => {
    // Mark as having unsaved changes
    setHasUnsavedChanges(true);

    // Clear any existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set debounce timeout (300ms)
    saveTimeoutRef.current = setTimeout(async () => {
      // Use functional update to get the latest entries state
      setEntries(currentEntries => {
        // Validate index
        if (idx < 0 || idx >= currentEntries.length) {
          console.error('Invalid index for auto-save:', idx, 'array length:', currentEntries.length);
          setHasUnsavedChanges(false);
          return currentEntries; // Return unchanged
        }
        
        // Check if data actually changed
        const currentEntry = currentEntries[idx];
        const hasChanges = JSON.stringify(formData) !== JSON.stringify(currentEntry);
        if (!hasChanges) {
          setHasUnsavedChanges(false);
          return currentEntries; // Return unchanged
        }

        // Set saving state
        setSavingEntry(idx);
        setSaveError(null);
        setSavedEntry(null);

        // Create updated array
        const newData = [...currentEntries];
        newData[idx] = formData;
        
        // Filter out any undefined/null items and ensure all items are valid objects
        const validData = newData.filter(item => item != null && typeof item === 'object');

        // Save asynchronously
        InventoryService.saveInventory(INVENTORY_TYPE, VERSION, validData)
          .then(() => {
            // Show success feedback
            setSavingEntry(null);
            setSavedEntry(idx);
            setTimeout(() => setSavedEntry(null), 1000);

            // Clear unsaved changes flag
            setHasUnsavedChanges(false);

            // Notify parent to update counters
            if (onListUpdate) {
              onListUpdate();
            }
          })
          .catch((error: any) => {
            console.error('Error auto-saving entry:', error);
            setSavingEntry(null);
            setSaveError(idx);
            
            // Check if it's an authentication error
            const errorMessage = error?.message || String(error);
            const isAuthError = errorMessage.includes('401') || 
                               errorMessage.includes('Not logged in') ||
                               errorMessage.includes('Token expired') ||
                               errorMessage.includes('Invalid token') ||
                               errorMessage.includes('No authentication token');
            
            if (isAuthError) {
              showAlert({
                title: 'Session Expired',
                message: 'Your session has expired. Please sign out and sign back in to continue saving changes.',
                okText: 'OK'
              });
            }
          });

        // Return updated entries immediately
        return validData;
      });
    }, 300);
  }, [onListUpdate, showAlert]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      if (periodicSaveIntervalRef.current) {
        clearInterval(periodicSaveIntervalRef.current);
      }
    };
  }, []);

  // Periodic auto-save (every 10 seconds when entry is expanded)
  useEffect(() => {
    // Clear any existing interval
    if (periodicSaveIntervalRef.current) {
      clearInterval(periodicSaveIntervalRef.current);
    }

    // Only start periodic save if we have an expanded entry
    if (expandedIndex !== null && form && user) {
      periodicSaveIntervalRef.current = setInterval(() => {
        autoSaveEntry(expandedIndex, form);
      }, 10000); // 10 seconds
    }

    return () => {
      if (periodicSaveIntervalRef.current) {
        clearInterval(periodicSaveIntervalRef.current);
      }
    };
  }, [expandedIndex, form, user, autoSaveEntry]);

  // Save on visibility change (when user switches browser tabs)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && expandedIndex !== null && form) {
        autoSaveEntry(expandedIndex, form);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [expandedIndex, form, autoSaveEntry]);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!form) return;
    const name = e.target.name;
    const value = e.target.value;
    
    // If changing date, check if it's already used (excluding current entry)
    if (name === 'date' && isDateUsed(value, expandedIndex ?? undefined)) {
      showAlert({
        message: 'An entry already exists for this date. Please choose a different date.',
        variant: 'warning'
      });
      return;
    }
    
    // Check if date is in the future
    if (name === 'date') {
      const today = new Date().toISOString().split('T')[0];
      if (value > today) {
        showAlert({
          message: 'You cannot select a future date.',
          variant: 'warning'
        });
        return;
      }
    }
    
    setForm({ ...form, [name]: value });
    // Trigger auto-save
    if (expandedIndex !== null) {
      autoSaveEntry(expandedIndex, { ...form, [name]: value });
    }
  };

  const handleFieldBlur = () => {
    // Do nothing here - form state already updated by handleFormChange
    // Actual saves happen on: periodic (10s), collapse, close, visibility change
  };

  const handleAddEntry = () => {
    // No limit on number of entries - user can practice as many days as they want
    // They only need 5 completed entries to finish the action

    const nextDate = getNextAvailableDate();
    const newEntry: InventoryEntry = {
      date: nextDate,
      note: ''
    };

    // Add new entry at the top (beginning of array)
    const updatedEntries = [newEntry, ...entries];
    // Filter out any undefined/null items and ensure all items are valid objects
    const validData = updatedEntries.filter(item => item != null && typeof item === 'object');
    
    // Don't sort - new entries go at the top
    setEntries(validData);
    
    // Save immediately
    if (user) {
      InventoryService.saveInventory(INVENTORY_TYPE, VERSION, validData).then(() => {
        if (onListUpdate) {
          onListUpdate();
        }
      }).catch(error => {
        console.error('Error saving new entry:', error);
      });
    }

    // New entry is at index 0 (top)
    setExpandedIndex(0);
    setForm({ ...newEntry });
  };

  const handleExpand = async (idx: number) => {
    // If we're expanding a different entry, save the current one first
    if (expandedIndex !== null && expandedIndex !== idx && form) {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      await immediateSaveEntry(expandedIndex, form);
    }
    
    // Toggle expansion
    if (expandedIndex === idx) {
      // Collapsing - save before collapsing
      if (form) {
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }
        const saved = await immediateSaveEntry(idx, form);
        if (!saved) {
          showAlert({
            message: 'Warning: Failed to save your changes. Please try editing again or check your connection.',
            variant: 'warning'
          });
        }
      }
      setExpandedIndex(null);
      setForm(null);
    } else {
      // Expanding - show edit form directly
      // Use functional update to get the latest entries state
      setEntries(currentEntries => {
        if (idx >= 0 && idx < currentEntries.length) {
          setExpandedIndex(idx);
          setForm({ ...currentEntries[idx] });
        }
        return currentEntries; // Return unchanged
      });
    }
  };

  const handleDelete = (idx: number) => {
    const entry = entries[idx];
    // Format date string (YYYY-MM-DD) to locale date string, avoiding timezone issues
    const dateStr = entry.date ? (() => {
      const [year, month, day] = entry.date.split('-');
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toLocaleDateString();
    })() : 'this entry';
    showConfirm({
      message: `Are you sure you want to remove the inventory entry for ${dateStr}?`,
      onConfirm: async () => {
        // If deleting the currently expanded entry, save any other changes first
        if (expandedIndex === idx && form) {
          if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
          }
        }
        
        const newData = entries.filter((_, i) => i !== idx);
        // Filter out any undefined/null items and ensure all items are valid objects
        const validData = newData.filter(item => item != null && typeof item === 'object');
        
        // Don't sort - keep the order as the user sees it
        setEntries(validData);

        // Save to API
        if (user) {
          try {
            await InventoryService.saveInventory(INVENTORY_TYPE, VERSION, validData);
            
            // Notify parent to update counters
            if (onListUpdate) {
              onListUpdate();
            }
            
            // If we deleted the expanded entry, clear expansion
            if (expandedIndex === idx) {
              setExpandedIndex(null);
              setForm(null);
            }
          } catch (error) {
            console.error('Error saving to API:', error);
          }
        }
      },
      variant: 'danger'
    });
  };

  // Handle modal close - save before closing
  const handleClose = async () => {
    // Clear any pending debounced save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Save current entry if expanded
    if (expandedIndex !== null && form) {
      const saved = await immediateSaveEntry(expandedIndex, form);
      
      if (!saved && hasUnsavedChanges) {
        // Save failed - warn user
        setSavingEntry(null);
        showConfirm({
          message: 'Failed to save your changes. If you close now, you will lose your recent work. Do you want to try again or close anyway?',
          onConfirm: () => {
            setHasUnsavedChanges(false);
            onClose();
          },
          confirmText: 'Close Anyway',
          cancelText: 'Try Again',
          variant: 'danger'
        });
        return;
      }
    }
    
    setExpandedIndex(null);
    setForm(null);
    setHasUnsavedChanges(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={handleClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 className={styles.title}>Step Ten: Daily Inventory Practice</h2>
          <button className={styles.closeButton} onClick={handleClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className={styles.content}>
          {!user ? (
            <div className={styles.loginPrompt}>
              <p>Please sign in to practice your daily inventory.</p>
            </div>
          ) : loading ? (
            <div className={styles.loading}>
              <p>Loading your inventory entries...</p>
            </div>
          ) : (
            <>
              <div className={styles.description}>
                <p>Practice taking a nightly inventory. You can add entries for as many days as you'd like. Each entry should include a date and brief notes about your day. Complete 5 entries to finish this action.</p>
              </div>

              {/* Add new entry button */}
              <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={handleAddEntry}
                  style={{
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: 4,
                    padding: '8px 16px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                  title="Add a new inventory entry"
                >
                  + Add Entry
                </button>
              </div>

              {/* List of entries */}
              {entries.length === 0 ? (
                <div className={styles.emptyList}>
                  <p>No inventory entries yet. Click "Add Entry" to get started.</p>
                </div>
              ) : (
                <div className={styles.inventoryList} style={{ maxHeight: '500px', overflowY: 'auto' }}>
                  {entries.length > 0 && (
                    <h4 className={styles.inventoryTitle}>
                      My Daily/Nightly Inventories
                      <span className={styles.inventoryCount}>
                        ({entries.length} {entries.length === 1 ? 'entry' : 'entries'}, {entries.filter(e => isEntryComplete(e)).length} of {REQUIRED_COMPLETED} complete)
                      </span>
                    </h4>
                  )}
                  <ul className={styles.inventoryItems}>
                    {entries.map((entry, idx) => {
                      const isExpanded = expandedIndex === idx;
                      const complete = isEntryComplete(entry);
                      // Format date string (YYYY-MM-DD) to locale date string with day of week, avoiding timezone issues
                      const dateStr = entry.date ? (() => {
                        const [year, month, day] = entry.date.split('-');
                        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                        const weekday = date.toLocaleDateString('en-US', { weekday: 'long' });
                        const dateFormatted = date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
                        return `${weekday} ${dateFormatted}`;
                      })() : 'No date';
                      
                      return (
                        <li key={idx} className={`${styles.inventoryItem} ${complete ? styles.completeEntry : styles.incompleteEntry}`}>
                          <div className={styles.itemSummary} onClick={() => handleExpand(idx)}>
                            <div className={styles.itemText}>
                              <span className={styles.expandIcon}>{isExpanded ? '▲' : '▼'}</span>
                              <span className={styles.itemLabel}>{dateStr}</span>
                              {savingEntry === idx && (
                                <span className={styles.savingText}>Saving...</span>
                              )}
                              {savedEntry === idx && (
                                <span className={styles.savedText}>✓ Saved</span>
                              )}
                              {saveError === idx && (
                                <span className={styles.saveErrorText}>⚠ Save failed</span>
                              )}
                              {savingEntry !== idx && savedEntry !== idx && saveError !== idx && (
                                <>
                                  {complete && (
                                    <span className={styles.completeIcon}>✓</span>
                                  )}
                                  {!complete && (
                                    <span className={styles.incompleteText}>click to see/add details</span>
                                  )}
                                </>
                              )}
                            </div>
                            <div className={styles.itemControls}>
                              <button 
                                onClick={e => { e.stopPropagation(); handleDelete(idx); }} 
                                className={styles.deleteButton}
                                title="Delete"
                              >
                                ×
                              </button>
                            </div>
                          </div>
                          
                          {/* Edit Form (inline, shown directly when expanded) */}
                          {isExpanded && form && (
                            <div className={styles.inventoryForm}>
                              <div className={styles.formGroup}>
                                <label>
                                  Date
                                </label>
                                <input 
                                  type="date"
                                  name="date" 
                                  value={form.date || ''} 
                                  onChange={handleFormChange}
                                  onBlur={handleFieldBlur}
                                  max={new Date().toISOString().split('T')[0]} // Prevent future dates
                                  required 
                                  style={{ width: '100%' }}
                                />
                              </div>
                              <div className={styles.formGroup}>
                                <label>Review of My Day</label>
                                <textarea 
                                  name="note" 
                                  placeholder="Was I resentful, selfish, dishonest, afraid? Do I owe an apology? Do I need to talk to my sponsor about something? Was I kind, loving, and patient with others? How did I practice recovery today?" 
                                  value={form.note || ''} 
                                  onChange={handleFormChange}
                                  onBlur={handleFieldBlur}
                                  rows={6} 
                                />
                              </div>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Step10InventoryModal;

